import { create } from 'zustand';

export interface OtpMetadata {
  deviceId?: string;
  preAuthSessionId?: string;
  expiresIn?: number;
  resendAvailableIn?: number;
  cooldownExpiresAt?: number;
}

type AsyncStatus = 'idle' | 'pending' | 'success' | 'error';

/**
 * MFA redirect path constant
 */
export const MFA_REDIRECT_PATH = '/otp';
export const MFA_RETURN_PATH_KEY = 'mfa:returnPath';

interface MfaState {
  mfaRequired: boolean;
  sendStatus: AsyncStatus;
  verifyStatus: AsyncStatus;
  error: string | null;
  otpMetadata: OtpMetadata | null;
  /** Flag to prevent duplicate redirects */
  redirectTriggered: boolean;
  /** Stored return path for after MFA completion */
  returnPath: string | null;
  setMfaRequired: (required: boolean) => void;
  setOtpMetadata: (metadata: OtpMetadata | null) => void;
  setSendStatus: (status: AsyncStatus, error?: string | null) => void;
  setVerifyStatus: (status: AsyncStatus, error?: string | null) => void;
  /** Trigger redirect to MFA page - returns true if redirect was triggered */
  triggerMfaRedirect: (currentPath?: string) => boolean;
  /** Clear the redirect flag (for testing or reset) */
  clearRedirectTriggered: () => void;
  reset: () => void;
}

const initialState: Pick<
  MfaState,
  'mfaRequired' | 'sendStatus' | 'verifyStatus' | 'error' | 'otpMetadata' | 'redirectTriggered' | 'returnPath'
> = {
  mfaRequired: false,
  sendStatus: 'idle',
  verifyStatus: 'idle',
  error: null,
  otpMetadata: null,
  redirectTriggered: false,
  returnPath: null,
};

/**
 * Build the return path from current location
 */
function buildReturnPath(): string {
  if (typeof window === 'undefined') {
    return '';
  }
  return `${window.location.pathname}${window.location.search}${window.location.hash}`;
}

export const useMfaStore = create<MfaState>((set, get) => ({
  ...initialState,

  setMfaRequired: (required) =>
    set((state) => ({
      ...state,
      mfaRequired: required,
      error: required ? state.error : null,
      // Reset redirect flag when MFA is no longer required
      redirectTriggered: required ? state.redirectTriggered : false,
    })),

  setOtpMetadata: (metadata) =>
    set((state) => ({
      ...state,
      otpMetadata: metadata
        ? {
            ...metadata,
            cooldownExpiresAt:
              metadata.cooldownExpiresAt ??
              (metadata.resendAvailableIn
                ? Date.now() + metadata.resendAvailableIn * 1000
                : undefined),
          }
        : null,
    })),

  setSendStatus: (status, error = null) =>
    set((state) => ({
      ...state,
      sendStatus: status,
      error,
    })),

  setVerifyStatus: (status, error = null) =>
    set((state) => ({
      ...state,
      verifyStatus: status,
      error,
    })),

  triggerMfaRedirect: (currentPath?: string) => {
    const state = get();

    // If already redirected or already on MFA page, don't redirect again
    if (state.redirectTriggered) {
      return false;
    }

    if (typeof window !== 'undefined') {
      const pathname = currentPath || window.location.pathname;

      // Don't redirect if already on MFA page
      if (pathname === MFA_REDIRECT_PATH) {
        return false;
      }

      // Store return path
      const returnPath = currentPath || buildReturnPath();
      try {
        window.localStorage?.setItem(MFA_RETURN_PATH_KEY, returnPath);
      } catch {
        // Ignore storage errors
      }

      // Set flag before redirect
      set({
        mfaRequired: true,
        redirectTriggered: true,
        returnPath,
      });

      // Use replace to avoid polluting browser history
      window.location.replace(MFA_REDIRECT_PATH);
      return true;
    }

    return false;
  },

  clearRedirectTriggered: () => {
    set({ redirectTriggered: false });
  },

  reset: () => set({ ...initialState }),
}));

/**
 * Check if MFA is currently blocking API calls
 * Use this before making API requests to avoid unnecessary failures
 */
export function isMfaBlocking(): boolean {
  return useMfaStore.getState().mfaRequired;
}

